import { colonyNetwork, hasBuilding, isShielded, logEvent, ownedSystems, visibleSectors } from './world.js';
import { scoutOnly } from './state.js';
import * as B from './balance.js';
export const ONBOARDING_MAX_TIER = 6;
export const TIER_LINK = 0, TIER_PRODUCE = 1, TIER_MARKET = 2, TIER_HOLD = 3, TIER_STRIKE = 4, TIER_TALK = 5, TIER_BEACONS = 6;
export function freshOnboarding(w, npc) {
    return npc || !w.rules.onboarding ? { tier: ONBOARDING_MAX_TIER, unlockedAt: [] } : { tier: 0, unlockedAt: [w.time] };
}
/** Lowest tier at which each command is accepted; commands not listed are open from the start. */
const COMMAND_TIER = {
    build: TIER_PRODUCE,
    market_order: TIER_MARKET, cancel_order: TIER_MARKET, barter_offer: TIER_MARKET, barter_accept: TIER_MARKET, decree: TIER_MARKET,
    route_set: TIER_MARKET, route_remove: TIER_MARKET, convoy_send: TIER_MARKET,
    train: TIER_HOLD, fleet_order: TIER_HOLD, focus: TIER_HOLD, split_fleet: TIER_HOLD, set_watch: TIER_HOLD,
    treaty: TIER_TALK, agent_mission: TIER_TALK, alliance_create: TIER_TALK, alliance_invite: TIER_TALK, alliance_join: TIER_TALK, alliance_leave: TIER_TALK,
    light_beacon: TIER_BEACONS, beacon_expedition_open: TIER_BEACONS,
};
const BUILDING_TIER = {
    tradepost: TIER_MARKET,
    shipyard: TIER_HOLD, bastion: TIER_HOLD, turret_light: TIER_HOLD, turret_heavy: TIER_HOLD, launcher: TIER_HOLD, refinery: TIER_HOLD, synthesizer: TIER_HOLD,
};
const STRIKE_ORDERS = new Set(['raid', 'blockade', 'ambush']);
/** The tier a command needs (0 when it is always open). */
export function commandTier(cmd) {
    if (cmd.type === 'build')
        return Math.max(TIER_PRODUCE, BUILDING_TIER[cmd.building] ?? 0);
    // The scout is the first-hour explorer (issue #48): trained from the first relay, sent anywhere from the start.
    if (cmd.type === 'train' && cmd.unit === 'scout')
        return TIER_PRODUCE;
    if (cmd.type === 'fleet_order')
        return STRIKE_ORDERS.has(cmd.order) ? TIER_STRIKE : TIER_HOLD;
    return COMMAND_TIER[cmd.type] ?? 0;
}
/** The tier a command needs from this colony: a fleet of scouts only moves from the start (issue #48). */
export function commandTierFor(w, cmd) {
    if (cmd.type === 'fleet_order' && (cmd.order === 'move' || cmd.order === 'return')) {
        const f = w.fleets[cmd.fleet];
        if (f && scoutOnly(f.units))
            return 0;
    }
    return commandTier(cmd);
}
/** `locked:<tier>` when the colony has not reached the command's tier, otherwise null. */
export function lockedReason(colony, cmd, w) {
    const need = w ? commandTierFor(w, cmd) : commandTier(cmd);
    return colony.onboarding.tier >= need ? null : `locked:${need}`;
}
function hasWarship(w, colony) {
    return Object.values(w.fleets).some((f) => f.owner === colony.id && f.units.corvette + f.units.frigate + f.units.cruiser > 0);
}
function inboundAlert(w, colony) {
    return w.events.some((e) => e.kind === 'fleet.inbound' && e.actors[1] === colony.id);
}
/** A neighbour in sight, human or NPC (Nick, 28.09: the NPCs already answer a pact, so they open diplomacy too). */
function neighbourSeen(w, colony) {
    const seen = visibleSectors(w, colony);
    return Object.values(w.colonies).some((c) => c.id !== colony.id && seen.has(w.galaxy.systems[c.capital].sector));
}
/** Whether the next tier's condition holds (docs/design/ONBOARDING-S0.md, table of tiers). */
function reached(w, colony, tier) {
    const age = w.time - colony.createdAt;
    switch (tier) {
        case TIER_PRODUCE: return Object.values(w.relays).some((r) => r.owner === colony.id);
        case TIER_MARKET: {
            // Three connected systems and one shared Draw lived; no hour of age any more (decision Nick, 30.09.2026: a player
            // back the next day after twelve minutes found the Market still shut).
            if (colony.scoreWindow.length === 0)
                return false;
            const net = colonyNetwork(w, colony);
            return ownedSystems(w, colony.id).filter((id) => net.has(id)).length >= 3;
        }
        case TIER_HOLD: {
            if (age < 6 * 3600)
                return false;
            const shieldLeft = colony.createdAt + B.shieldHours(w.seasonEndsAt / 86400) * 3600 - w.time;
            return hasBuilding(w, colony.capital, 'shipyard') || inboundAlert(w, colony) || shieldLeft < 12 * 3600;
        }
        case TIER_STRIKE: return !isShielded(w, colony) && hasWarship(w, colony);
        case TIER_TALK: {
            return age >= 86400 && neighbourSeen(w, colony);
        }
        case TIER_BEACONS: {
            const net = colonyNetwork(w, colony);
            if (ownedSystems(w, colony.id).filter((id) => net.has(id)).length >= 10)
                return true;
            const seen = visibleSectors(w, colony);
            return w.galaxy.beacons.some((b) => seen.has(w.galaxy.systems[b]?.sector ?? ''));
        }
        default: return false;
    }
}
/** The next tier and what it still waits for, from the same facts as `reached` (null at the last tier). */
export function onboardingNext(w, colony) {
    const tier = colony.onboarding.tier + 1;
    if (tier > ONBOARDING_MAX_TIER)
        return null;
    const age = w.time - colony.createdAt, needs = [];
    const connected = () => { const net = colonyNetwork(w, colony); return ownedSystems(w, colony.id).filter((id) => net.has(id)).length; };
    const nextDraw = (Math.floor(w.time / B.DRAW_INTERVAL_S) + 1) * B.DRAW_INTERVAL_S;
    switch (tier) {
        case TIER_PRODUCE:
            needs.push({ kind: 'relay' });
            break;
        case TIER_MARKET: {
            // The tier is checked at the Draw: three connected systems and one shared Draw lived (no hour of age, 30.09).
            if (colony.scoreWindow.length === 0)
                needs.push({ kind: 'draw', at: nextDraw });
            const have = connected();
            if (have < 3)
                needs.push({ kind: 'connected', have, need: 3 });
            break;
        }
        case TIER_HOLD: {
            if (age < 6 * 3600)
                needs.push({ kind: 'age', at: colony.createdAt + 6 * 3600 });
            const shieldLeft = colony.createdAt + B.shieldHours(w.seasonEndsAt / 86400) * 3600 - w.time;
            if (!hasBuilding(w, colony.capital, 'shipyard') && !inboundAlert(w, colony) && shieldLeft >= 12 * 3600)
                needs.push({ kind: 'shipyard' });
            break;
        }
        case TIER_STRIKE:
            if (isShielded(w, colony))
                needs.push({ kind: 'unshielded', at: colony.createdAt + B.shieldHours(w.seasonEndsAt / 86400) * 3600 });
            if (!hasWarship(w, colony))
                needs.push({ kind: 'warship' });
            break;
        case TIER_TALK: {
            if (age < 86400)
                needs.push({ kind: 'age', at: colony.createdAt + 86400 });
            if (!neighbourSeen(w, colony))
                needs.push({ kind: 'neighbour' });
            break;
        }
        case TIER_BEACONS:
            if (!reached(w, colony, TIER_BEACONS))
                needs.push({ kind: 'beacon', have: connected(), need: 10 });
            break;
    }
    return { tier, needs };
}
/**
 * Raise the colony's tier while the next condition holds. Called at every Draw and around every command; each
 * unlock leaves a journal line (`onboarding.<tier>`) and an event (`onboarding.unlocked`) the client turns into
 * the General's first word on the new screen.
 */
export function advanceOnboarding(w, colony) {
    let unlocked = 0;
    while (colony.onboarding.tier < ONBOARDING_MAX_TIER && reached(w, colony, colony.onboarding.tier + 1)) {
        unlockTier(w, colony, colony.onboarding.tier + 1);
        unlocked++;
    }
    return unlocked;
}
function unlockTier(w, colony, tier) {
    colony.onboarding.tier = tier;
    colony.onboarding.unlockedAt[tier] = w.time;
    if (colony.npc)
        return;
    colony.journal.push({ at: w.time, kind: `onboarding.${tier}` });
    if (colony.journal.length > B.JOURNAL_MAX)
        colony.journal.splice(0, colony.journal.length - B.JOURNAL_MAX);
    logEvent(w, 'onboarding.unlocked', [colony.id], { tier });
}
/** "Show me everything": a veteran sees every tab at once and trades from the start (the Market tier), but the
 *  Shipyard, the fleets, diplomacy and the Beacons keep their own conditions (decision Nick, 30.09.2026, after the
 *  calibration playtest: « tout ouvrir » opened the military six hours early). */
export function unlockAll(w, colony) {
    if (colony.onboarding.skipped || colony.npc)
        return;
    colony.onboarding.skipped = true;
    for (let t = colony.onboarding.tier + 1; t <= TIER_MARKET; t++)
        colony.onboarding.unlockedAt[t] = w.time;
    colony.onboarding.tier = Math.max(colony.onboarding.tier, TIER_MARKET);
    colony.journal.push({ at: w.time, kind: 'onboarding.all' });
    logEvent(w, 'onboarding.unlocked', [colony.id], { tier: colony.onboarding.tier, all: true });
}
//# sourceMappingURL=onboarding.js.map